import { useLykaApi } from '@/services/lyka-api'
import { formatMoney } from '@/utils/formatMoney'

export interface Coupon {
  code: string
  discount: number
  referrer?: {
    uuid: string
    name?: string
  }
  discount_type: 'percentage' | 'dollar'
  multi_box_discount_enabled: boolean
  multi_box_discounts: MultiBoxDiscount[]
}

interface MultiBoxDiscount {
  box: number
  discount: number
}

interface InvalidCouponResponse {
  data: []
  message: string
}

interface ValidCouponResponse {
  data: {
    coupon_code: string
    discount: number
    referrer?: {
      uuid: string
    }
    discount_amount_type: 'percentage' | 'dollar'
    multi_box_discount_enabled: boolean
    multi_box_discounts: MultiBoxDiscount[]
    validate_message: string
    firstname?: string
  }
}

export const getCoupon = async (couponCode: string): Promise<Coupon | undefined> => {
  try {
    const { data } = await useLykaApi().post<InvalidCouponResponse | ValidCouponResponse>('coupons/validate', {
      couponCode,
    })

    // If data is an empty array it means the coupon is invalid
    if (Array.isArray(data)) {
      return undefined
    }

    return {
      code: data.coupon_code,
      discount: data.discount,
      referrer: data.referrer
        ? {
            uuid: data.referrer?.uuid,
            name: data.firstname,
          }
        : undefined,
      multi_box_discount_enabled: data.multi_box_discount_enabled,
      multi_box_discounts: data.multi_box_discounts,
      discount_type: data.discount_amount_type,
    }
  } catch {
    return undefined
  }
}

export const formatDiscount = (discount: number, type: 'percentage' | 'dollar'): string => {
  if (type === 'percentage') {
    return `${discount}%`
  }

  return formatMoney(discount)
}

export const getCouponDiscounts = (coupon: Coupon): number[] => {
  if (coupon.multi_box_discounts?.length) {
    return coupon.multi_box_discounts.map((discount) => discount.discount)
  }

  return [coupon.discount]
}

export const getButtonHTML = (coupon: Coupon): string => {
  const discounts = getCouponDiscounts(coupon)

  const [first, ...others] = discounts

  const equal = others.every((d) => d === first)

  if (equal) {
    return `Claim <output>${formatDiscount(first, coupon.discount_type)} off</output> your first ${
      discounts.length > 1 ? `${discounts.length} deliveries` : 'delivery'
    }`
  }

  const [second] = others

  return `Claim <output>${formatDiscount(first, coupon.discount_type)} off</output> + <output>${formatDiscount(
    second,
    coupon.discount_type
  )} off</output>`
}
